import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, ErrorBanner, Spinner } from '../components/ui.js';
import { api } from '../lib/api.js';
import { useAction } from '../lib/hooks.js';

/**
 * Accept an organization invitation.
 *
 * The organization is derived from the invitation server-side and the
 * invitation's email must match the signed-in user's, so a leaked link cannot
 * be redeemed by someone else. This page only reports the outcome.
 */
export function AcceptInvitationPage(): ReactNode {
  const { invitationId = '' } = useParams();
  const navigate = useNavigate();
  const { run, pending, error } = useAction();
  const [done, setDone] = useState(false);

  useEffect(() => {
    void run(async () => {
      const result = await api.post<{ organizationId: string; role: string }>(
        `/api/invitations/${invitationId}/accept`,
      );
      setDone(true);
      setTimeout(() => void navigate(`/orgs/${result.organizationId}`), 900);
    });
    // Runs once for this invitation id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitationId]);

  return (
    <div className="container container-narrow">
      <Card title="Invitation">
        {pending && (
          <div className="row muted">
            <Spinner />
            <span>Accepting your invitation…</span>
          </div>
        )}
        {error && (
          <div className="stack">
            <ErrorBanner message={error} />
            <p className="small muted">
              Invitations expire after 48 hours and can only be accepted by the address they were
              sent to. Ask an admin to send a new one.
            </p>
          </div>
        )}
        {done && <div className="banner banner-success">You are in. Taking you there…</div>}
      </Card>
    </div>
  );
}
